import { useEffect, useState } from 'react'

import type { GetStaticPropsContext, GetStaticPropsResult } from 'next'
import { useSession } from 'next-auth/react'
import { NextSeo } from 'next-seo'

import menuData from '@data/menu.json'

import getGtmEcommerceProduct from '@lib/analytics/ecommerce'
import { gtmEvent } from '@lib/analytics/gtm'
import { lang } from '@lib/constants'
import { getCustomerFavorites } from '@lib/operations/customer-favorites'
import { getProductBySlug, getProductRecommendationsById } from '@lib/operations/product'
import type { Breadcrumbs, Product, ProductCard, Product as ProductType } from '@lib/types'
import { getProductBreadcrumbs } from '@lib/utils/breadcrumbs'
import { isCustomizableBoxVariant } from '@lib/utils/isSubscriptionBox'

import ProductView from '@components/product/ProductView'

interface Props {
	product: ProductType
	breadcrumbs: Breadcrumbs | null
	recommendations: ProductCard[]
}

export default function ProductPage({ product, breadcrumbs, recommendations }: Props) {
	const { data: session } = useSession()
	const [isFavorite, setIsFavorite] = useState<boolean>()

	// GTM
	useEffect(() => {
		// Analytics *****
		const gtmEcommerceProduct = getGtmEcommerceProduct(
			product.variants && product.variants.length > 0
				? {
						product,
						price: product.variants[0].price.amount,
						productBreadcrumb: breadcrumbs,
						isSubscriptionBox: !!isCustomizableBoxVariant(product.variants[0].id)
				  }
				: {
						product,
						price: '',
						productBreadcrumb: breadcrumbs,
						isSubscriptionBox: false
				  }
		)

		gtmEvent({
			event: 'analytics_event',
			data: {
				eventData: {
					category: 'ecommerce',
					action: 'productDetail'
				},
				ecommerce: {
					currencyCode: product.variants[0].price.currencyCode,
					detail: {
						actionField: { list: breadcrumbs?.[0]?.title || undefined },
						products: [gtmEcommerceProduct]
					}
				}
			}
		})
		// Analytics *****
	}, [])

	// Get the customer's favorite products
	useEffect(() => {
		const getFavorites = async () => {
			const customerFavorites: string[] =
				(await getCustomerFavorites((session as any)?.shopifyAccessToken as string)) || []
			setIsFavorite(customerFavorites.includes(product.id))
		}

		getFavorites()
	}, [product.id, (session as any)?.shopifyAccessToken])

	return (
		<>
			<NextSeo title={product.name} />

			{/* Why is this a component? It's only used here */}
			<ProductView
				product={product}
				breadcrumbs={breadcrumbs}
				recommendations={recommendations}
				isFavorite={isFavorite}
			/>
		</>
	)
}

export async function getStaticProps({
	params
}: Required<GetStaticPropsContext<{ slug: Product['slug'] }>>): Promise<
	GetStaticPropsResult<Props>
> {
	const product = await getProductBySlug(params.slug ?? '')

	// IF PRODUCT IS IN MEMBERS MARKET --> HIDE from products/[slug]
	if (product?.collections?.some((collection) => collection.handle === 'member-market')) {
		return { notFound: true }
	}

	if (product === null) {
		return { notFound: true }
	}

	if (!!product.tags && product.tags.includes('Subscription Box')) {
		const destinationPaths: Record<typeof lang, string> = {
			'en-us': '/membership',
			'es-es': '/suscribete',
			'en-gb': '/subscribe'
		}

		return {
			redirect: {
				permanent: true,
				destination: destinationPaths[lang]
			}
		}
	}

	const productCollectionsPaths = product.collections.map((collection) => collection.handle)
	const breadcrumbs = getProductBreadcrumbs(productCollectionsPaths, menuData.items) ?? null
	const recommendations = await getProductRecommendationsById(product.id)

	return {
		props: { product, breadcrumbs, recommendations }
	}
}

export async function getStaticPaths() {
	// Pages are refreshed using on-demand ISR
	return {
		paths: [],
		fallback: 'blocking'
	}
}
